import {Inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {ServerEndpoints} from '../../../server.endpoints';
import {DecommissionReasonOption, DeviceLifecycle} from '../shared/models/device-lifecycle.model';

/**
 * Thin HTTP client for the DeviceService fiscal-lifecycle API (`/api/devices/v2026.6/lifecycle`). Requests go
 * through the gateway under the SPA's single `BASE_URL` origin, so the auth interceptor attaches the context token
 * automatically (mirrors {@link DeviceConfigHttpService}). Keyed by a device's fiscal client id, which the device
 * settings page reads from the monolith device's `clientId`.
 */
@Injectable({providedIn: 'root'})
export class DeviceLifecycleHttpService {
  constructor(private http: HttpClient, @Inject('BASE_URL') private baseUrl: string) {
  }

  private get base(): string {
    return this.baseUrl + ServerEndpoints.Device_Lifecycle__v2026_6;
  }

  /** The selectable decommission reasons with their display labels. */
  reasons(): Observable<DecommissionReasonOption[]> {
    return this.http.get<DecommissionReasonOption[]>(`${this.base}/reasons`);
  }

  /** Read a device's fiscal lifecycle. */
  get(clientId: string): Observable<DeviceLifecycle> {
    return this.http.get<DeviceLifecycle>(`${this.base}/${clientId}`);
  }

  /**
   * Set the acquisition date as a plain calendar day (`yyyy-MM-dd`, e.g. "2025-03-14"). DeviceService stores that
   * day as it is, so send the day, never an instant, which would carry the browser's zone.
   */
  setAcquiredOn(clientId: string, acquiredOn: string): Observable<DeviceLifecycle> {
    return this.http.put<DeviceLifecycle>(`${this.base}/${clientId}/acquired-on`, {acquiredOn});
  }

  /** Decommission a device with a structured reason (and a note for the "Other" reason). */
  decommission(clientId: string, reason: number, note?: string | null): Observable<DeviceLifecycle> {
    return this.http.post<DeviceLifecycle>(`${this.base}/${clientId}/decommission`, {reason, note: note ?? null});
  }

  /** Re-attempt the fiscal-provider push for a device whose last sync failed (troubleshooting). */
  resync(clientId: string): Observable<DeviceLifecycle> {
    return this.http.post<DeviceLifecycle>(`${this.base}/${clientId}/resync`, {});
  }
}
